// pages/Dashboard/Reporte/Cuentas.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../../../utils/api';
import { useEmpresaSelector } from '../../../context/EmpresaSelectorContext';
import EmpresaBadge from '../../../components/EmpresaBadge';
import * as XLSX from 'xlsx';
import 'bulma/css/bulma.min.css';

export default function ReporteCuentas() {
   const { empresaSeleccionada } = useEmpresaSelector();
   const [cierresCaja, setCierresCaja] = useState([]); // para resumen de ingresos
   const [egresos, setEgresos] = useState([]);
   const [cxc, setCxc] = useState([]); // cuentas por cobrar
   const [cxp, setCxp] = useState([]); // cuentas por pagar
   const [loading, setLoading] = useState(true);

  const currentYear = new Date().getFullYear();
  const [anioSeleccionado, setAnioSeleccionado] = useState(''); // Inicializar vacío para mostrar todos por defecto
  const [mesSeleccionado, setMesSeleccionado] = useState('');
  const [diaSeleccionado, setDiaSeleccionado] = useState('');

  const anios = useMemo(() => {
    const arr = [];
    for (let y = currentYear; y >= currentYear - 5; y--) arr.push(y.toString());
    return arr;
  }, [currentYear]);

  const meses = [
    'enero','febrero','marzo','abril','mayo','junio',
    'julio','agosto','septiembre','octubre','noviembre','diciembre'
  ];

  const dias = Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0'));

  useEffect(() => {
    const cargar = async () => {
      try {
        setLoading(true);
        const empresaId = empresaSeleccionada?.id && empresaSeleccionada.id !== 'all' ? empresaSeleccionada.id : null;
        const qs = empresaId ? `?empresaId=${empresaId}` : '';
        const rCierres = await apiRequest(`/cierres-caja${qs}`);
        if (rCierres.ok) setCierresCaja(await rCierres.json()); else setCierresCaja([]);
        const rEg = await apiRequest(`/egresos${qs}`);
        if (rEg.ok) setEgresos(await rEg.json()); else setEgresos([]);
        const rCxc = await apiRequest(`/cuentas-por-cobrar${qs}`);
        if (rCxc.ok) setCxc(await rCxc.json()); else setCxc([]);
        const rCxp = await apiRequest(`/cuentas-por-pagar${qs}`);
        if (rCxp.ok) setCxp(await rCxp.json()); else setCxp([]);
      } catch (e) {
        console.error('Error cargando cuentas:', e);
        setCierresCaja([]); setEgresos([]); setCxc([]); setCxp([]);
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, [empresaSeleccionada?.id]);

  // Helpers de fecha
  function matchFechaISO(fechaStr) {
    if (!fechaStr) return false;
    const d = fechaStr.includes('T') ? fechaStr.split('T')[0] : fechaStr; // yyyy-mm-dd
    const [y, m, dday] = d.split('-');
    const mesNombre = meses[Number(m) - 1];
    const matchAnio = !anioSeleccionado || y === anioSeleccionado;
    const matchMes = !mesSeleccionado || mesNombre === mesSeleccionado;
    const matchDia = !diaSeleccionado || dday === diaSeleccionado;
    return matchAnio && matchMes && matchDia;
  }

  // Función para obtener fecha de un item (similar a ResumenTotal)
  const getFecha = (item) => {
    const fecha = item.fechaFactura || item.fechaVencimiento || item.fecha;
    if (!fecha) return { anio: "", mes: "", dia: "" };

    try {
      const date = new Date(fecha);
      // Ajustar por zona horaria
      const offset = date.getTimezoneOffset();
      const adjustedDate = new Date(date.getTime() + (offset * 60 * 1000));

      return {
        anio: adjustedDate.getFullYear().toString(),
        mes: (adjustedDate.getMonth() + 1).toString().padStart(2, '0'),
        dia: adjustedDate.getDate().toString().padStart(2, '0')
      };
    } catch (e) {
      console.error('Error al procesar fecha:', e);
      return { anio: "", mes: "", dia: "" };
    }
  };

  // Filtrados usando la nueva función getFecha
  const ingresosFiltrados = cierresCaja.filter(c => {
    if (!c.fecha) return false;
    const { anio, mes, dia } = getFecha(c);
    const matchAnio = !anioSeleccionado || anio === anioSeleccionado;
    const matchMes = !mesSeleccionado || mes === mesSeleccionado.padStart(2, '0');
    const matchDia = !diaSeleccionado || dia === diaSeleccionado.padStart(2, '0');
    return matchAnio && matchMes && matchDia && c.tipoPago !== 'porPagar'; // solo ventas normales
  });

  const egresosFiltrados = egresos.filter(e => {
    if (!e.fecha) return false;
    const { anio, mes, dia } = getFecha(e);
    const matchAnio = !anioSeleccionado || anio === anioSeleccionado;
    const matchMes = !mesSeleccionado || mes === mesSeleccionado.padStart(2, '0');
    const matchDia = !diaSeleccionado || dia === diaSeleccionado.padStart(2, '0');
    return matchAnio && matchMes && matchDia;
  });

  const cxcFiltradas = cxc.filter(c => {
    const { anio, mes, dia } = getFecha(c);
    const matchAnio = !anioSeleccionado || anio === anioSeleccionado;
    const matchMes = !mesSeleccionado || mes === mesSeleccionado.padStart(2, '0');
    const matchDia = !diaSeleccionado || dia === diaSeleccionado.padStart(2, '0');
    return matchAnio && matchMes && matchDia;
  });

  const cxpFiltradas = cxp.filter(c => {
    const { anio, mes, dia } = getFecha(c);
    const matchAnio = !anioSeleccionado || anio === anioSeleccionado;
    const matchMes = !mesSeleccionado || mes === mesSeleccionado.padStart(2, '0');
    const matchDia = !diaSeleccionado || dia === diaSeleccionado.padStart(2, '0');
    return matchAnio && matchMes && matchDia;
  });

  // Cálculos de totales para Ingresos (desde CierreCaja)
  // Filtrar cierresCaja por fecha para obtener el total pagado correcto (igual al de CierreCaja)
  const cierresFiltrados = cierresCaja.filter(c => {
    if (!c.fecha) return false;
    const { anio, mes, dia } = getFecha(c);
    const matchAnio = !anioSeleccionado || anio === anioSeleccionado;
    const matchMes = !mesSeleccionado || mes === mesSeleccionado.padStart(2, '0');
    const matchDia = !diaSeleccionado || dia === diaSeleccionado.padStart(2, '0');
    return matchAnio && matchMes && matchDia;
  });
  const totalIngresosPagado = cierresFiltrados.reduce((sum, c) => {
    if (c.tipoPago === 'porPagar') {
      return sum + parseFloat(c.abono || 0);
    } else {
      return sum + parseFloat(c.pagado || 0);
    }
  }, 0);
  const totalIngresosEfectivo = ingresosFiltrados.filter(i => i.tipoPago === 'efectivo').reduce((sum, i) => sum + (i.pagado ?? i.total ?? 0), 0);
  const totalIngresosTransferencias = ingresosFiltrados.filter(i => i.tipoPago === 'transferencia').reduce((sum, i) => sum + (i.pagado ?? i.total ?? 0), 0);

  // Para ventas por pagar: calcular adeudado y abonado
  const ventasPorPagar = cierresCaja.filter(c => {
    if (!c.fecha) return false;
    const { anio, mes, dia } = getFecha(c);
    const matchAnio = !anioSeleccionado || anio === anioSeleccionado;
    const matchMes = !mesSeleccionado || mes === mesSeleccionado.padStart(2, '0');
    const matchDia = !diaSeleccionado || dia === diaSeleccionado.padStart(2, '0');
    return matchAnio && matchMes && matchDia && c.tipoPago === 'porPagar';
  });
  const totalAdeudado = ventasPorPagar.reduce((sum, v) => {
    const costo = parseFloat(v.costo || 0);
    const cantidad = parseFloat(v.cantidad || 1);
    const iva = parseFloat(v.iva || 0) / 100;
    const totalCostoIva = (costo * cantidad) * (1 + iva);
    const abono = parseFloat(v.abono || 0);
    return sum + (totalCostoIva - abono);
  }, 0);
  const totalAbonado = ventasPorPagar.reduce((sum, v) => sum + parseFloat(v.abono || 0), 0);

  // Cálculos para Egresos
  const totalEgresos = egresosFiltrados.reduce((sum, e) => sum + (e.monto ?? e.saldo ?? 0), 0);
  const totalEgresosEfectivo = egresosFiltrados.filter(e => e.tipoPago === 'efectivo').reduce((sum, e) => sum + (e.monto ?? e.saldo ?? 0), 0);
  const totalEgresosTransferencias = egresosFiltrados.filter(e => e.tipoPago === 'transferencia').reduce((sum, e) => sum + (e.monto ?? e.saldo ?? 0), 0);

  // Cálculos para Cuentas por Pagar
  const totalFacturasCXP = cxpFiltradas.length;
  const totalAPagarCXP = cxpFiltradas.reduce((sum, c) => sum + (c.saldoAdeudado || 0), 0);
  const vencidasCXP = cxpFiltradas.filter(c => new Date(c.fechaVencimiento) < new Date()).length;

  // Cálculos para Cuentas por Cobrar
  const totalCuentasCXC = cxcFiltradas.length;
  const totalACobrarCXC = cxcFiltradas.reduce((sum, c) => sum + (c.saldoCancelado || 0), 0);
  const totalInteresesCXC = cxcFiltradas.reduce((sum, c) => {
    const sub = parseFloat(c.importeTotal || c.total || 0);
    const sal = parseFloat(c.saldoCancelado || 0);
    return sum + Math.max(0, sal - sub);
  }, 0);

  const exportarExcel = () => {
    const wsIngresos = XLSX.utils.json_to_sheet(ingresosFiltrados.map((i) => ({
      descripcion: i.producto || i.descripcion || 'Venta',
      monto: i.pagado ?? i.total ?? 0,
      efectivo: i.tipoPago === 'efectivo' ? (i.pagado ?? i.total ?? 0) : 0,
      banco: i.tipoPago === 'transferencia' ? (i.pagado ?? i.total ?? 0) : 0,
      fecha: i.fecha ? new Date(i.fecha).toLocaleDateString() : 'N/A',
    })));
    const wsEgresos = XLSX.utils.json_to_sheet(egresosFiltrados.map((e) => ({
      descripcion: e.descripcion,
      monto: e.monto ?? e.saldo ?? 0,
      tipoPago: e.tipoPago ?? '-',
      banco: e.banco ?? '-',
      categoria: e.categoria ?? '-',
      fecha: e.fecha ? new Date(e.fecha).toLocaleDateString() : 'N/A',
    })));
    const wsCxc = XLSX.utils.json_to_sheet(cxcFiltradas.map((c) => ({
      cliente: c.cliente,
      importeTotal: c.importeTotal,
      interes: (c.saldoCancelado - c.importeTotal) || 0,
      fechaFactura: c.fechaFactura ? new Date(c.fechaFactura).toLocaleDateString() : 'N/A',
      fechaVencimiento: c.fechaVencimiento ? new Date(c.fechaVencimiento).toLocaleDateString() : 'N/A',
      saldoCancelado: c.saldoCancelado,
    })));
    const wsCxp = XLSX.utils.json_to_sheet(cxpFiltradas.map((c) => ({
      proveedor: c.proveedor,
      importeTotal: c.importeTotal,
      interes: (c.saldoAdeudado - c.importeTotal) || 0,
      fechaFactura: c.fechaFactura ? new Date(c.fechaFactura).toLocaleDateString() : 'N/A',
      fechaVencimiento: c.fechaVencimiento ? new Date(c.fechaVencimiento).toLocaleDateString() : 'N/A',
      saldoAdeudado: c.saldoAdeudado,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsIngresos, 'Ingresos');
    XLSX.utils.book_append_sheet(wb, wsEgresos, 'Egresos');
    XLSX.utils.book_append_sheet(wb, wsCxc, 'CuentasCobrar');
    XLSX.utils.book_append_sheet(wb, wsCxp, 'CuentasPagar');
    const nameParts = [anioSeleccionado];
    if (mesSeleccionado) nameParts.push(mesSeleccionado);
    if (diaSeleccionado) nameParts.push(diaSeleccionado);
    XLSX.writeFile(wb, `Reporte_Cuentas_${nameParts.join('_') || 'Todos'}.xlsx`);
  };

  return (
    <div className="p-4 cuentas-container" style={{ maxWidth: 1150, margin: '0 auto' }}>
      <style>{`
        /* Compacto como Patrimonio */
        .cuentas-table td, .cuentas-table th { padding: 2px 2px !important; line-height: 1.05 !important; font-size: 1.2rem; }
        .cuentas-table tbody tr { height: 22px; }
        .cuentas-trunc { display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        /* Ajustes para todas las tablas de cuentas */
        .cuentas-container .table td, .cuentas-container .table th {
          padding: 2px 2px !important;
          font-size: 1.1rem !important;
          line-height: 1.2 !important;
        }
        .cuentas-container .table tbody tr { height: 28px; }
      `}</style>
      <h2 className="title is-4 has-text-black">Cuentas (Cobrar, Pagar y Egresos)</h2>
      <EmpresaBadge />

      {/* Filtros */}
      <div className="columns is-vcentered mb-4 is-centered" style={{ maxWidth: 900, margin: '0 auto' }}>
        <div className="column is-3">
          <label className="label">Año</label>
          <div className="select is-fullwidth">
            <select value={anioSeleccionado} onChange={(e) => setAnioSeleccionado(e.target.value)}>
              {anios.map(anio => <option key={anio} value={anio}>{anio}</option>)}
            </select>
          </div>
        </div>
        <div className="column is-3">
          <label className="label">Mes</label>
          <div className="select is-fullwidth">
            <select value={mesSeleccionado} onChange={(e) => setMesSeleccionado(e.target.value)}>
              <option value="">Todos</option>
              {meses.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>
        <div className="column is-3">
          <label className="label">Día</label>
          <div className="select is-fullwidth">
            <select value={diaSeleccionado} onChange={(e) => setDiaSeleccionado(e.target.value)}>
              <option value="">Todos</option>
              {dias.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
        </div>
        <div className="column is-2" style={{ display: 'flex', alignItems: 'flex-end' }}>
          <button className="button is-light is-fullwidth" onClick={() => { setAnioSeleccionado(''); setMesSeleccionado(''); setDiaSeleccionado(''); }}>Reset</button>
        </div>
      </div>

      <div className="field is-grouped is-grouped-right mb-3">
        <p className="control">
          <button className="button is-small is-light" onClick={exportarExcel}>Descargar Excel</button>
        </p>
      </div>

      {/* Ingresos */}
      <div className="box">
        <h3 className="subtitle is-5">Ingresos (diarios)</h3>
        {loading ? (
          <div className="notification is-info has-text-centered">Cargando...</div>
        ) : (
          <div className="table-container">
            <table className="table is-striped cuentas-table" style={{ width: '100%', marginBottom: 0, tableLayout: 'auto' }}>
              <thead>
                <tr className="has-background-darkblue has-text-gold">
                  <th className="has-text-centered">Total Pagado</th>
                  <th className="has-text-centered">Efectivo</th>
                  <th className="has-text-centered">Transferencias</th>
                  <th className="has-text-centered">Total Adeudado</th>
                  <th className="has-text-centered">Total Abonado</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="has-text-centered has-text-weight-bold">${totalIngresosPagado.toFixed(2)}</td>
                  <td className="has-text-centered">${totalIngresosEfectivo.toFixed(2)}</td>
                  <td className="has-text-centered">${totalIngresosTransferencias.toFixed(2)}</td>
                  <td className="has-text-centered has-text-danger">${totalAdeudado.toFixed(2)}</td>
                  <td className="has-text-centered">${totalAbonado.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Cuentas por Cobrar */}
      <div className="box">
        <h3 className="subtitle is-5">Cuentas por Cobrar</h3>
        {loading ? (
          <div className="notification is-info has-text-centered">Cargando...</div>
        ) : (
          <div className="table-container">
            <table className="table is-striped" style={{ width: '100%', marginBottom: 0, tableLayout: 'auto', fontSize: '1rem' }}>
              <thead>
                <tr className="has-background-darkblue has-text-gold">
                  <th className="has-text-centered">Total Cuentas</th>
                  <th className="has-text-centered">Total a Cobrar</th>
                  <th className="has-text-centered">Total Intereses</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="has-text-centered has-text-weight-bold">{totalCuentasCXC}</td>
                  <td className="has-text-centered has-text-weight-bold has-text-danger">${totalACobrarCXC.toFixed(2)}</td>
                  <td className="has-text-centered has-text-weight-bold">${totalInteresesCXC.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Egresos */}
      <div className="box">
        <h3 className="subtitle is-5">Egresos</h3>
        {loading ? (
          <div className="notification is-info has-text-centered">Cargando...</div>
        ) : (
          <div className="table-container">
            <table className="table is-striped" style={{ width: '100%', marginBottom: 0, tableLayout: 'auto', fontSize: '1rem' }}>
              <thead>
                <tr className="has-background-darkblue has-text-gold">
                  <th className="has-text-centered">Total Egresos</th>
                  <th className="has-text-centered">Efectivo</th>
                  <th className="has-text-centered">Transferencias</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="has-text-centered has-text-weight-bold">${totalEgresos.toFixed(2)}</td>
                  <td className="has-text-centered">${totalEgresosEfectivo.toFixed(2)}</td>
                  <td className="has-text-centered">${totalEgresosTransferencias.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Cuentas por Pagar */}
      <div className="box">
        <h3 className="subtitle is-5">Cuentas por Pagar</h3>
        {loading ? (
          <div className="notification is-info has-text-centered">Cargando...</div>
        ) : (
          <div className="table-container">
            <table className="table is-striped" style={{ minWidth: 900, marginBottom: 0, tableLayout: 'auto' }}>
              <thead>
                <tr className="has-background-darkblue has-text-gold">
                  <th className="has-text-centered">Total Facturas</th>
                  <th className="has-text-centered">Total a Pagar</th>
                  <th className="has-text-centered">Vencidas</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="has-text-centered has-text-weight-bold">{totalFacturasCXP}</td>
                  <td className="has-text-centered has-text-weight-bold has-text-danger">${totalAPagarCXP.toFixed(2)}</td>
                  <td className="has-text-centered has-text-weight-bold has-text-danger">{vencidasCXP}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
